import { useState } from 'react';
import { Key, ToggleLeft, ToggleRight, Trash2, Copy, Plus, RefreshCw, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetEnvironmentConfigQuery,
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useGetFeatureFlagsQuery,
  useToggleFeatureFlagMutation,
} from '@/store/api/apiSlice';
import type { EnvironmentConfig, ApiKey, CreatedApiKey, FeatureFlag } from '@/types';
import { format } from 'date-fns';

export function SettingsPage() {
  const [newKeyName, setNewKeyName] = useState('');
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: envConfig, isLoading: envLoading } = useGetEnvironmentConfigQuery(undefined);
  const { data: apiKeys, isLoading: keysLoading } = useGetApiKeysQuery(undefined);
  const { data: featureFlags, isLoading: flagsLoading } = useGetFeatureFlagsQuery(undefined);
  const [createApiKey, { isLoading: isCreatingKey }] = useCreateApiKeyMutation();
  const [revokeApiKey, { isLoading: isRevoking }] = useRevokeApiKeyMutation();
  const [toggleFlag, { isLoading: isToggling }] = useToggleFeatureFlagMutation();

  const configList = (envConfig as EnvironmentConfig[] | undefined) ?? [];
  const keysList = (apiKeys as ApiKey[] | undefined) ?? [];
  const flagsList = (featureFlags as FeatureFlag[] | undefined) ?? [];

  async function handleCreateKey() {
    if (!newKeyName.trim()) return;
    const result = await createApiKey({ name: newKeyName.trim(), policyIds: [] }).unwrap();
    setCreatedKey(result as CreatedApiKey);
    setNewKeyName('');
  }

  function handleCloseCreateDialog() {
    setKeyDialogOpen(false);
    setCreatedKey(null);
  }

  async function handleCopyKey() {
    if (!createdKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text for manual copy
    }
  }

  async function handleRevoke() {
    if (revokeTarget) {
      await revokeApiKey(revokeTarget.id).unwrap();
      setRevokeTarget(null);
    }
  }

  async function handleToggleFlag(flag: FeatureFlag) {
    await toggleFlag({ key: flag.key, enabled: !flag.enabled }).unwrap();
  }

  function isKeyActive(key: ApiKey): boolean {
    return !key.revokedAt;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground">System configuration and management</p>
      </div>

      {/* Environment Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Environment Configuration</CardTitle>
          <CardDescription>Current runtime configuration values</CardDescription>
        </CardHeader>
        <CardContent>
          {envLoading ? (
            <LoadingSpinner className="py-4" />
          ) : configList.length === 0 ? (
            <p className="text-sm text-muted-foreground">No configuration available</p>
          ) : (
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2 text-left font-medium">Key</th>
                    <th className="px-4 py-2 text-left font-medium">Value</th>
                    <th className="px-4 py-2 text-left font-medium">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {configList.map((item) => (
                    <tr key={item.key} className="border-b last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{item.key}</td>
                      <td className="px-4 py-2 font-mono text-xs max-w-xs truncate">{item.value}</td>
                      <td className="px-4 py-2">
                        <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{item.source}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="h-4 w-4" />
                API Keys
              </CardTitle>
              <CardDescription>Manage API keys for external integrations</CardDescription>
            </div>
            <Button size="sm" onClick={() => setKeyDialogOpen(true)}>
              <Plus className="mr-1 h-3 w-3" />
              Create Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {keysLoading ? (
            <LoadingSpinner className="py-4" />
          ) : keysList.length === 0 ? (
            <EmptyState
              title="No API keys"
              description="Create an API key to enable external access"
              icon={<Key className="h-8 w-8" />}
            />
          ) : (
            <div className="space-y-3">
              {keysList.map((key) => {
                const active = isKeyActive(key);
                return (
                  <div key={key.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <p className="text-sm font-medium">{key.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {key.keyPrefix}… • Created {format(new Date(key.createdAt), 'MMM d, yyyy')}
                        {key.lastUsedAt && ` • Last used ${format(new Date(key.lastUsedAt), 'MMM d, yyyy')}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {active ? (
                        <span className="text-xs text-green-600">Active</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Revoked</span>
                      )}
                      {active && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setRevokeTarget(key)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Feature Flags */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Feature Flags</CardTitle>
          <CardDescription>Toggle experimental features on or off</CardDescription>
        </CardHeader>
        <CardContent>
          {flagsLoading ? (
            <LoadingSpinner className="py-4" />
          ) : flagsList.length === 0 ? (
            <p className="text-sm text-muted-foreground">No feature flags configured</p>
          ) : (
            <div className="space-y-3">
              {flagsList.map((flag) => (
                <div key={flag.key} className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium">{flag.key}</p>
                    <p className="text-xs text-muted-foreground">{flag.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleFlag(flag)}
                    disabled={isToggling}
                  >
                    {flag.enabled ? (
                      <ToggleRight className="mr-1 h-5 w-5 text-green-600" />
                    ) : (
                      <ToggleLeft className="mr-1 h-5 w-5 text-muted-foreground" />
                    )}
                    {flag.enabled ? 'On' : 'Off'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={keyDialogOpen} onOpenChange={(open) => { if (!open) handleCloseCreateDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {createdKey ? 'API Key Created' : 'Create API Key'}
            </DialogTitle>
            <DialogDescription>
              {createdKey
                ? 'Copy this key now — it will not be shown again.'
                : 'Give the API key a descriptive name to identify its purpose.'}
            </DialogDescription>
          </DialogHeader>

          {createdKey ? (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/50 p-3">
                <p className="text-xs text-muted-foreground mb-1">API Key (one-time display)</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono break-all select-all bg-background rounded px-2 py-1.5 border">
                    {createdKey.key}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 h-8 w-8"
                    onClick={handleCopyKey}
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-amber-600">
                ⚠ Store this key securely. For security, it cannot be retrieved again.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="e.g., CI/CD Pipeline"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
            </div>
          )}

          <DialogFooter>
            {createdKey ? (
              <Button onClick={handleCloseCreateDialog}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleCloseCreateDialog}>
                  Cancel
                </Button>
                <Button onClick={handleCreateKey} disabled={isCreatingKey || !newKeyName.trim()}>
                  {isCreatingKey ? 'Creating...' : 'Create'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke API Key Confirmation */}
      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title="Revoke API Key"
        description={`Are you sure you want to revoke "${revokeTarget?.name}"? Any services using this key will lose access.`}
        confirmLabel="Revoke"
        variant="destructive"
        onConfirm={handleRevoke}
        isLoading={isRevoking}
      />
    </div>
  );
}
